// src/ConsumerApp/Screens/Account/views/InvoicesView.js

import React, { useMemo, useState } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import NoteIcon from '../../../../assets/icons/Note.svg';
import DownloadSimple from '../../../../assets/icons/DownloadSimple.svg';
import FileTextIcon from '../../../../assets/icons/FileText.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';

// Custom Hooks
import {
  useGetCustomerInvoices,
  useDownloadInvoice,
} from '../../../api/invoices/invoice.hooks';

export default function InvoicesView() {
  const [downloadingId, setDownloadingId] = useState(null);

  // 1. Session Context (Direct storage lookup)
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const token = customer?.token || sessionStorage.getItem('@auth_token') || localStorage.getItem('@auth_token');

  // 2. TanStack Query & Download Mutation
  const {
    data: invoices = [],
    isLoading,
    refetch,
    isRefetching,
  } = useGetCustomerInvoices(customerId);

  const downloadMutation = useDownloadInvoice();

  // 3. Formatters[cite: 40]
  const formatDate = (isoString) => {
    if (!isoString) return 'Date unavailable';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // 4. Download Handler[cite: 40]
  const handleDownload = async (item) => {
    const files = item.files || [];
    if (!files || files.length === 0) {
      alert(`No PDF file attached to invoice ${item.invoice_number || ''}.`);
      return;
    }

    setDownloadingId(item._id);
    try {
      await downloadMutation.mutateAsync({
        fileRelativePath: files[0],
        invoiceNumber: item.invoice_number || 'Invoice',
        token,
      });
    } catch (err) {
      alert(err.message || 'Could not download invoice. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>Invoices & Receipts</h2>
          <span style={styles.headerSubtitle}>
            Download GST-compliant tax invoices and billing receipts
          </span>
        </div>

        <button
          type="button"
          style={styles.refreshBtn}
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <img
            src={ClockCounterClockwise}
            alt="refresh"
            style={{
              width: 14,
              height: 14,
              opacity: 0.7,
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>{isRefetching ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      {/* ── Content Body ── */}
      {isLoading ? (
        <div style={styles.loadingBox}>
          <span>Fetching tax invoices...</span>
        </div>
      ) : invoices.length === 0 ? (
        <div style={styles.emptyBox}>
          <div style={styles.emptyIconCircle}>
            <img src={FileTextIcon} alt="invoices" style={{ width: 22, height: 22, opacity: 0.4 }} />
          </div>
          <h4 style={styles.emptyTitle}>No invoices available</h4>
          <p style={styles.emptySubtitle}>
            Tax invoices are generated once materials are billed and dispatched to your site.
          </p>
        </div>
      ) : (
        <div style={styles.cardContainer}>
          <div style={styles.cardHeader}>
            <span style={styles.cardHeaderLabel}>Invoice Summary</span>
            <span style={styles.countBadge}>{invoices.length} invoices</span>
          </div>

          <div style={styles.invoicesList}>
            {invoices.map((item, index) => {
              const isLast = index === invoices.length - 1;
              const isItemDownloading = downloadingId === item._id;

              return (
                <div
                  key={item._id || index}
                  style={{
                    ...styles.itemRow,
                    borderBottom: isLast ? 'none' : '1px solid #E2E8F0',
                  }}
                >
                  {/* Left: Document Icon & Order/Invoice ID[cite: 42] */}
                  <div style={styles.leftCol}>
                    <div style={styles.iconFrame}>
                      <img src={NoteIcon} alt="note" style={styles.noteSvg} />
                    </div>

                    <div style={styles.detailsCol}>
                      <span style={styles.orderIdText}>{item.invoice_number || 'Tax Invoice'}</span>
                      <span style={styles.dateTimeText}>{formatDate(item.invoice_date)}</span>
                    </div>
                  </div>

                  {/* Right: Amount & Download Trigger[cite: 42] */}
                  <div style={styles.rightCol}>
                    <span style={styles.amountText}>{formatAmount(item.total)}</span>

                    <button
                      type="button"
                      style={styles.downloadBtn}
                      onClick={() => handleDownload(item)}
                      disabled={isItemDownloading}
                    >
                      <img
                        src={DownloadSimple}
                        alt="download"
                        style={{
                          width: 15,
                          height: 15,
                          filter:
                            'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                        }}
                      />
                      <span style={styles.downloadText}>
                        {isItemDownloading ? 'Downloading...' : 'Download'}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: '600',
    color: '#2B2A29',
    cursor: 'pointer',
    outline: 'none',
  },
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    backgroundColor: '#F8F6F2',
    borderBottom: '1px solid #CBD5E1',
  },
  cardHeaderLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
  },
  countBadge: {
    fontSize: 11.5,
    fontWeight: '600',
    color: '#707B8C',
  },
  invoicesList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    transition: 'background-color 0.15s ease',
  },
  leftCol: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    minWidth: 0,
  },
  iconFrame: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  noteSvg: {
    width: 20,
    height: 20,
    filter:
      'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
  },
  detailsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  orderIdText: {
    fontSize: 14.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  dateTimeText: {
    fontSize: 12,
    color: '#707B8C',
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 6,
    flexShrink: 0,
  },
  amountText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#2B2A29',
  },
  downloadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
  },
  downloadText: {
    fontSize: 12.5,
    fontWeight: '700',
    color: '#C7061F',
  },
  loadingBox: {
    padding: '50px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13.5,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '4px 0 0 0',
    fontSize: 12.5,
    color: '#707B8C',
    maxWidth: 320,
    lineHeight: '18px',
  },
};